// Client Signing Pass: the client signs every intake document across all packets
// in ONE continuous sitting. Full document text shown read-only (they sign what
// they read), pad below, auto-advance. Staff completes + PIN-signs afterward.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SignaturePad from './SignaturePad';
import PlanRepFormPanel from './PlanRepFormPanel';
import { resolveTokens } from '../EMRNotes';

const API = 'https://tcio-api.onrender.com';

export default function ClientSigningPass({ patientId, episodeId, packetKeys, clientNameDefault, facilityName, rates, identity, onClose }) {
  const [queue, setQueue] = useState(null);
  const [idx, setIdx] = useState(0);
  const [clientName, setClientName] = useState(clientNameDefault || '');
  const [sigImg, setSigImg] = useState(null);
  const [padKey, setPadKey] = useState(0); // remount pad to clear between forms
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState(false);
  const planSign = React.useRef(null); // set by the insurance plan form when this document has one
  const planReady = React.useCallback((fn) => { planSign.current = fn; }, []);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/emr/patients/${patientId}/client-pass-queue`,
      { params: { packet_keys: packetKeys.join(','), episode_id: episodeId || '' } });
    setQueue(r.data);
    const first = r.data.docs.findIndex(d => !d.client_signed);
    setIdx(first === -1 ? r.data.docs.length : first);
  }, [patientId, episodeId, packetKeys]);

  useEffect(() => { load().catch(() => setErr('Could not load the signing queue')); }, [load]);

  if (!queue) return (
    <div style={S.overlay}><div style={{ color: '#fff', fontSize: 15 }}>{err || 'Loading signing pass…'}</div></div>
  );

  const done = idx >= queue.docs.length;
  const doc = done ? null : queue.docs[idx];
  const signedCount = queue.docs.filter(d => d.client_signed).length;

  // Document text = the form's display/prompt content, read-only
  const docText = doc ? (doc.fields || [])
    .filter(f => f.type === 'display' && (f.text || f.prompt))
    .map(f => resolveTokens(f.text || f.prompt, facilityName, rates, identity)) : [];

  const signAndAdvance = async () => {
    setErr('');
    if (!clientName.trim()) { setErr('Enter the client name first'); return; }
    if (!sigImg) { setErr('Please sign in the box'); return; }
    setBusy(true);
    try {
      if (doc.form_key === 'authorized_representative' && planSign.current) await planSign.current(sigImg, clientName.trim());
      let noteId = doc.note_id;
      if (!noteId) {
        // Client can sign before staff has opened the form - create the draft on the fly
        const r = await axios.post(`${API}/api/emr/notes`, {
          patient_id: patientId, episode_id: episodeId,
          note_type: doc.note_type, form_key: doc.form_key, content: {} });
        noteId = r.data.id;
      }
      await axios.post(`${API}/api/emr/notes/${noteId}/client-signature`,
        { image_base64: sigImg, client_name: clientName.trim() });
      // advance
      const updated = { ...queue };
      updated.docs = queue.docs.map((d, i) => i === idx ? { ...d, note_id: noteId, client_signed: true } : d);
      setQueue(updated);
      setSigImg(null); setPadKey(k => k + 1);
      setFlash(true);
      setTimeout(() => {
        setFlash(false);
        const next = updated.docs.findIndex((d, i) => i > idx && !d.client_signed);
        setIdx(next === -1 ? updated.docs.length : next);
      }, 700);
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || 'Could not save signature'); }
    setBusy(false);
  };

  return (
    <div style={S.overlay}>
      <div style={S.sheet}>
        <div style={S.header}>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#336090' }}>
            Client Signing Pass · {signedCount} of {queue.total} signed
          </div>
          <button onClick={onClose} style={S.close}>Save & Exit</button>
        </div>
        <div style={S.progressWrap}><div style={{ ...S.progressBar, width: `${(signedCount / Math.max(queue.total, 1)) * 100}%` }} /></div>

        {done ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#15803d' }}>✓ Client signing complete</div>
            <div style={{ fontSize: 14, color: '#64748b', marginTop: 8 }}>{queue.total} of {queue.total} documents signed. Staff can now complete and countersign each form.</div>
            <button onClick={onClose} style={{ ...S.primary, marginTop: 20 }}>Done</button>
          </div>
        ) : (
          <div style={S.body}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Document {idx + 1} of {queue.total}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: '4px 0 12px' }}>{doc.title}</div>
            <div style={S.docBox}>
              {docText.length
                ? docText.map((t, i) => <p key={i} style={{ margin: '0 0 10px', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{t}</p>)
                : <p style={{ margin: 0, fontSize: 13.5, color: '#64748b' }}>Staff will review the contents of this document with you. Your signature below acknowledges it.</p>}
            </div>
            {doc.form_key === 'authorized_representative' && <PlanRepFormPanel mode="client" patientId={patientId} episodeId={episodeId} onReady={planReady} />}
            <div style={{ margin: '14px 0 6px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>CLIENT NAME</div>
            <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="First Last"
              style={S.input} />
            <div style={{ margin: '12px 0 6px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>SIGN BELOW</div>
            <SignaturePad key={padKey} onChange={setSigImg} height={140} />
            {err && <div style={{ color: '#dc2626', fontSize: 13, fontWeight: 700, marginTop: 8 }}>{err}</div>}
            {flash && <div style={{ color: '#15803d', fontSize: 14, fontWeight: 800, marginTop: 8 }}>✓ Signed</div>}
            <button onClick={signAndAdvance} disabled={busy}
              style={{ ...S.primary, marginTop: 14, width: '100%' }}>
              {busy ? 'Saving…' : `Sign & Continue →`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,22,35,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  sheet: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 760, maxHeight: '94vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#fff', zIndex: 2 },
  close: { background: 'transparent', border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, color: '#64748b', cursor: 'pointer' },
  progressWrap: { height: 5, background: '#f1f5f9' },
  progressBar: { height: 5, background: '#4479B0', transition: 'width 0.4s' },
  body: { padding: '18px 22px 26px' },
  docBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', maxHeight: 260, overflow: 'auto' },
  input: { width: '100%', maxWidth: 320, padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' },
  primary: { background: '#4479B0', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 20px', fontSize: 15, fontWeight: 800, cursor: 'pointer' },
};
